import React, { forwardRef, PropsWithChildren } from "react";
import { styled, Text, View, XStack } from "tamagui";

import { InfoIcon } from "../../../icons";
import { withIconRef } from "../../../utils/with-icon-ref";
import { Button, ButtonBaseProps, ButtonRef, Card } from "../../ui";

export const DisclaimerCard: React.FC<
  PropsWithChildren<{ show: boolean; Icon?: React.FC }>
> = ({ children, show, Icon = DisclaimerIcon }) => {
  if (!show) return null;

  return (
    <Card paddingVertical="$3" $sm={{ padding: "$2", margin: "$3" }}>
      <XStack
        gap="$1.5"
        alignItems="center"
        justifyContent="space-between"
        $sm={{ flexDirection: "column" }}
      >
        <View>
          <Icon />
        </View>
        <XStack
          gap={"$2"}
          $gtSm={{ gap: "$5" }}
          flex={1}
          alignItems="center"
          justifyContent="space-between"
          $sm={{ flexDirection: "column" }}
        >
          {children}
        </XStack>
      </XStack>
    </Card>
  );
};

const DisclaimerText = styled(Text, {
  fontFamily: "$body",
  fontWeight: "$5",
  fontSize: 20,
  lineHeight: 30,
  $sm: {
    textAlign: "center",
    fontSize: 18,
    lineHeight: 24,
  },
});

const DisclaimerIcon = styled(withIconRef(InfoIcon), {
  color: "$error-500",
  width: 32,
  height: 32,
});

const DisclaimerButton = forwardRef<ButtonRef, ButtonBaseProps>(
  (props, ref) => {
    return (
      <Button
        ref={ref}
        variant="primary"
        size="md"
        rounded="lg"
        minWidth={180}
        backgroundColor="$pinkmain-500"
        hoverStyle={{ backgroundColor: "$darkpink-500" }}
        {...props}
      />
    );
  },
);

const getLocalStorageValue = (initial: boolean, key?: string) => {
  if (!key) return initial;
  if (typeof window === "undefined") return initial;
  const value = window.localStorage.getItem(key);
  if (value === null) return initial;
  return value === "true";
};

const setLocalStorageValue = (value: boolean, key?: string) => {
  if (!key) return;
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, value ? "true" : "false");
};

interface UseShowStateParams {
  /** Initial show state */
  initial?: boolean;
  /** Key to use for localStorage to persist show state */
  localStorageKey?: string;
}
const useShowState = ({
  initial = true,
  localStorageKey,
}: UseShowStateParams) => {
  const [show, setShow] = React.useState(
    getLocalStorageValue(initial, localStorageKey),
  );

  const handleClose = React.useCallback(() => {
    setShow(false);
    setLocalStorageValue(false, localStorageKey);
  }, [localStorageKey]);

  return { show, setShow, handleClose };
};

export const Disclaimer = {
  Card: DisclaimerCard,
  Icon: DisclaimerIcon,
  Text: DisclaimerText,
  Button: DisclaimerButton,
  useShowState,
};
